/* Pure helpers for the email inbox.
   Relative timestamps deliberately reuse `relativeTime` from domainMetrics.js —
   do not add another copy here. */

/* SpamAssassin-style score: 5+ is the conventional spam threshold, and 2+ is
   "suspicious" (used only for the badge colour, never for filtering). */
export const SPAM_THRESHOLD = 5;

/** 'spam' | 'clean' | 'unknown' — the bucket the filter chips work on. */
export const spamBucket = (score) => {
  const n = Number(score);
  if (score == null || score === '' || Number.isNaN(n)) return 'unknown';
  return n >= SPAM_THRESHOLD ? 'spam' : 'clean';
};

/** Badge `state` for a spam score. */
export const spamState = (score) => {
  const n = Number(score);
  if (Number.isNaN(n)) return 'info';
  if (n >= SPAM_THRESHOLD) return 'danger';
  if (n >= 2) return 'warn';
  return 'success';
};

/** Collapse an email body to a single-line preview. */
export const snippet = (text, maxLength = 90) => {
  if (!text) return '';
  const flat = String(text)
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (flat.length <= maxLength) return flat;
  return `${flat.slice(0, maxLength).trimEnd()}…`;
};

/** Display name out of an RFC-822 style sender. */
export const senderName = (sender) => {
  if (!sender) return '(unknown sender)';
  const match = String(sender).match(/^"?([^"<]+)"?\s*</);
  if (match) return match[1].trim();
  return String(sender).split('@')[0] || String(sender);
};

/** Bare address out of an RFC-822 style sender. */
export const senderAddress = (sender) => {
  if (!sender) return '';
  const match = String(sender).match(/<([^>]+)>/);
  return (match ? match[1] : String(sender)).trim();
};

/** Does this email match a free-text subject/sender query? */
export const matchesQuery = (email, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = `${email?.subject || ''} ${email?.sender || ''}`.toLowerCase();
  return haystack.includes(q);
};

/** "Re: " prefixed mailto: URL for replying to an email. */
export const replyMailto = (email) => {
  const to = senderAddress(email?.sender);
  if (!to) return '';
  const subject = String(email?.subject || '').trim();
  const reSubject = /^re:\s/i.test(subject) ? subject : `Re: ${subject}`;
  return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(reSubject)}`;
};

/* ── client-side "seen" set ───────────────────────────────────────────────
   The backend has no read flag, so read state lives in localStorage only.
   Every access is wrapped: private windows and blocked site data throw. */

export const SEEN_STORAGE_KEY = 'emails_seen_ids';
const SEEN_CAP = 2000;

/** Set of ids (as strings) the user has opened. Never throws. */
export const loadSeen = (storage) => {
  const store = storage !== undefined ? storage : safeStorage();
  try {
    const raw = store && store.getItem(SEEN_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map(String));
  } catch {
    return new Set();
  }
};

/** Persist the seen set (newest-last, capped). Returns true when written. */
export const saveSeen = (seen, storage) => {
  const store = storage !== undefined ? storage : safeStorage();
  try {
    if (!store) return false;
    const ids = [...seen].map(String).slice(-SEEN_CAP);
    store.setItem(SEEN_STORAGE_KEY, JSON.stringify(ids));
    return true;
  } catch {
    return false;
  }
};

function safeStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** How many of `emails` are not in the seen set. */
export const unseenCount = (emails, seen) =>
  (emails || []).reduce((n, e) => (seen && seen.has(String(e.id)) ? n : n + 1), 0);

/* Reader feedback: the note a reader leaves under an article after a thumbs
   up/down, written by the sites as type "page_feedback". */
export const FEEDBACK_TYPE = 'page_feedback';
export const isFeedback = (email) => email?.type === FEEDBACK_TYPE;

/* The contact page's form (type "contact_form"). Like reader feedback it is
   someone writing on purpose, so both get the alert badge and a handled flag. */
export const CONTACT_TYPE = 'contact_form';
export const ALERT_TYPES = [FEEDBACK_TYPE, CONTACT_TYPE];

/** Inbox filter key -> the message type the backend filters on. */
export const FILTER_TYPES = { feedback: FEEDBACK_TYPE, contact: CONTACT_TYPE };

/** Does this message get Mark handled? (Alert types, or anything already flagged.) */
export const tracksHandled = (email) =>
  Boolean(email) && (ALERT_TYPES.includes(email.type) || typeof email.handled === 'boolean');

/** Share of votes that were 👍 (0..1), or null with no votes. */
export const helpfulShare = ({ up = 0, down = 0 } = {}) => (up + down > 0 ? up / (up + down) : null);

/** A domain id (bistrogarden_com) as a hostname (bistrogarden.com). */
export const domainLabel = (projectId) => String(projectId || '').replace(/_/g, '.');
